import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { useJWTVExp } from '@/components/experiments/JWTVTvodExp';
import { getJWTVTitleUrl, openJwTvRentalModal } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { ClickoutUrl, safeOpen } from '@/helpers/clickout-helper';
import { useRoutingStore, useUserStore } from '@/helpers/composables/useStores';

import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { WatchNowOfferFragment } from '@/pages/graphql/fragments/WatchNowOffer.fragment';
import { Domain } from '@/routing/helpers/subdomain-helper';
import { useRouter } from 'vue-router/composables';

export function useRentalModal() {
	const router = useRouter();
	const { activeDomain } = useRoutingStore();
	const { isAccountVerified } = useUserStore();

	const { userExpCode } = useJWTVExp();

	function openRentalModal({
		title,
		offer,
		forceSafeOpen,
		clickoutUrl,
		overrideRedirectUrl,
		fromQTV,
	}: {
		title: TitleDetail;
		offer: WatchNowOfferFragment | TitleOfferFragment;
		clickoutUrl: ClickoutUrl;

		forceSafeOpen?: boolean;
		overrideRedirectUrl?: boolean;

		//EXP_JWTV_TVOD
		fromQTV?: boolean;
	}) {
		const isMainDomain = activeDomain.value === Domain.MAIN;
		const linkContextId = clickoutUrl.contexts?.LinkContext?.link?.self!;
		if (forceSafeOpen || !isMainDomain) {
			const newStandardWebURL = new URL(offer.standardWebURL || '');
			newStandardWebURL.searchParams.set('lcId', linkContextId);
			const newStandardWebURLString = newStandardWebURL.href;
			safeOpen(newStandardWebURLString);
			return;
		}
		if ((isAccountVerified.value && !title?.content?.fullPath) || overrideRedirectUrl) {
			let overrideSuccessUrl: string | null = null;

			// EXP_JW_TVOD
			const activatePromoCode = fromQTV ? userExpCode.value : undefined;

			if (overrideRedirectUrl) {
				overrideSuccessUrl = getJWTVTitleUrl(
					title.content.fullPath,
					linkContextId,
					activatePromoCode?.queryParam
				);
			}

			openJwTvRentalModal({
				modalProps: {
					title: title!,
					offer: offer,
					additionalContexts: [
						clickoutUrl.contexts.LinkContext,
						clickoutUrl.contexts.ClickoutContextGraphql,
						clickoutUrl.contexts.TitleContextGraphql,
					],
					overrideSuccessUrl,
					promoCode: activatePromoCode?.promoCode,
				},
			});
		} else {
			router.push({
				path: title.content.fullPath,
				query: {
					package: 'jwt',
					...(linkContextId
						? {
								lcId: linkContextId,
						  }
						: {}),
				},
			});
		}
	}

	return {
		openRentalModal,
	};
}
